import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import CustomTooltip from "./custom-tooltip";
import EsIndexDialog from "./es-index-dialog";
import EsFieldDialog from "./es-field-dialog";
import EsAliasDialog from "./es-alias-dialog";
import {
  EsOntologyAlias,
  EsOntologyField,
  EsOntologyIndex,
  useDeleteEsAlias,
  useDeleteEsField,
  useDeleteEsIndex,
  useGetEsOntologyAliases,
  useGetEsOntologyFields,
  useGetEsOntologyIndexes,
} from "@/service-hooks/use-get-es-ontology-service";

interface EsOntologySectionProps {
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  isViewer?: boolean;
}

const PAGE_SIZE = 5;

/**
 * Indexes panel for an Elastic-type DataLake. Mirrors the DDL Tables → Columns
 * pattern: the ES Indexes card lists selectable index rows, and selecting one
 * reveals sibling Fields and Aliases cards below (instead of inline expansion).
 * Returns a fragment of sibling cards so they pick up the parent `space-y-4`
 * spacing, exactly like the DDL Tables/Columns/Indexes cards.
 */
export default function EsOntologySection({
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  isViewer,
}: EsOntologySectionProps) {
  const indexesQuery = useGetEsOntologyIndexes(dataLakeId, true, { projectUuid });
  const deleteIndex = useDeleteEsIndex();

  const [selectedIndexId, setSelectedIndexId] = useState<string | null>(null);
  const [indexPage, setIndexPage] = useState(1);
  const [indexDialogOpen, setIndexDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<EsOntologyIndex | undefined>();

  const indexes = indexesQuery.data ?? [];
  const selectedIndex = selectedIndexId
    ? indexes.find((i) => i.id === selectedIndexId) ?? null
    : null;

  // Reset selection when the data lake changes so we don't show a stale child panel.
  useEffect(() => {
    setSelectedIndexId(null);
    setIndexPage(1);
  }, [dataLakeId]);

  const indexTotal = indexes.length;
  const indexTotalPages = Math.max(1, Math.ceil(indexTotal / PAGE_SIZE));
  const indexStart = (indexPage - 1) * PAGE_SIZE;
  const indexPageItems = indexes.slice(indexStart, indexStart + PAGE_SIZE);

  const toggleSelect = (id?: string) => {
    if (!id) return;
    setSelectedIndexId((prev) => (prev === id ? null : id));
  };

  const onClickAddIndex = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setEditingIndex(undefined);
    setIndexDialogOpen(true);
  };

  const onClickEditIndex = (idx: EsOntologyIndex) => {
    setEditingIndex(idx);
    setIndexDialogOpen(true);
  };

  const onClickDeleteIndex = async (idx: EsOntologyIndex) => {
    if (!idx.id) return;
    try {
      await deleteIndex.mutateAsync({ id: idx.id, projectUuid, dataLakeId });
      // Clear selection if the deleted index was the one being viewed.
      setSelectedIndexId((prev) => (prev === idx.id ? null : prev));
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <>
      {/* ES Indexes card — selectable rows (mirrors the DDL Tables card) */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">ES Indexes</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${indexPageItems.length} of ${indexTotal}`}
            </span>
            {dataLakeLabel && (
              <span className="text-sm text-white truncate">— {dataLakeLabel}</span>
            )}
            {indexesQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add ES Index">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickAddIndex();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {indexes.length === 0 ? (
          indexesQuery.isFetching ? (
            <div className="text-xs text-muted text-center py-3 flex items-center justify-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading indexes...
            </div>
          ) : (
            <div className="text-xs text-muted text-center py-3">
              No ES indexes yet. Click + to add one, or upload mapping/settings JSON when creating the data lake.
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {indexPageItems.map((idx) => {
              const isSelected = !!idx.id && selectedIndexId === idx.id;
              return (
                <div
                  key={idx.id ?? idx.name}
                  className={`relative group flex items-center justify-between px-3 py-2.5 rounded border transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "border-surface-border bg-surface-active hover:bg-surface-hover"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSelect(idx.id)}
                    className="flex-1 min-w-0 text-left text-white"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="text-base font-medium truncate min-w-0"
                        title={idx.name}
                      >
                        {idx.name}
                      </span>
                      <span className="text-xs text-muted whitespace-nowrap shrink-0">
                        {idx.fieldCount ?? 0} field
                        {(idx.fieldCount ?? 0) === 1 ? "" : "s"}
                        {(idx.aliasNames?.length ?? 0) > 0
                          ? ` · ${idx.aliasNames!.length} alias${
                              idx.aliasNames!.length === 1 ? "" : "es"
                            }`
                          : ""}
                      </span>
                      {(idx.shards || idx.replicas) && (
                        <span className="text-xs text-muted whitespace-nowrap shrink-0">
                          {idx.shards ? `shards:${idx.shards}` : ""}
                          {idx.shards && idx.replicas ? " " : ""}
                          {idx.replicas ? `replicas:${idx.replicas}` : ""}
                        </span>
                      )}
                    </div>
                  </button>
                  {!isViewer && (
                    <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <CustomTooltip text="Edit Index">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickEditIndex(idx);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </CustomTooltip>
                      <CustomTooltip text="Delete Index">
                        <Button
                          variant="destructive"
                          size="icon"
                          disabled={!idx.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickDeleteIndex(idx);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {indexTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {indexPage} of {indexTotalPages} ({indexPageItems.length} indexes this page) • Total: {indexTotal} indexes
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIndexPage((p) => Math.max(1, p - 1))}
                disabled={indexPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIndexPage((p) => Math.min(indexTotalPages, p + 1))}
                disabled={indexPage === indexTotalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Fields & Aliases cards — sibling cards, only when an index is selected */}
      {selectedIndex && selectedIndex.id && (
        <EsIndexChildren
          projectUuid={projectUuid}
          dataLakeId={dataLakeId}
          indexId={selectedIndex.id}
          indexName={selectedIndex.name}
          isViewer={isViewer}
        />
      )}

      <EsIndexDialog
        isOpen={indexDialogOpen}
        onClose={() => setIndexDialogOpen(false)}
        projectUuid={projectUuid}
        dataLakeId={dataLakeId}
        existing={editingIndex}
      />
    </>
  );
}

interface EsIndexChildrenProps {
  projectUuid: string;
  dataLakeId: string;
  indexId: string;
  indexName?: string;
  isViewer?: boolean;
}

/**
 * Fields and Aliases panels for the selected ESIndex, rendered as two separate
 * sibling cards below the ES Indexes list — the same shape as the DDL Columns
 * card that appears after selecting a table. Both lists paginate client-side.
 */
function EsIndexChildren({
  projectUuid,
  dataLakeId,
  indexId,
  indexName,
  isViewer,
}: EsIndexChildrenProps) {
  const fieldsQuery = useGetEsOntologyFields(dataLakeId, true, {
    projectUuid,
    indexId,
    limit: 1000,
  });
  const aliasesQuery = useGetEsOntologyAliases(dataLakeId, true, {
    projectUuid,
    indexId,
    limit: 1000,
  });
  const deleteField = useDeleteEsField();
  const deleteAlias = useDeleteEsAlias();

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<EsOntologyField | undefined>();
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState<EsOntologyAlias | undefined>();

  const [fieldPage, setFieldPage] = useState(1);
  const [aliasPage, setAliasPage] = useState(1);

  // Reset pagination whenever the selected index changes.
  useEffect(() => {
    setFieldPage(1);
    setAliasPage(1);
  }, [indexId]);

  const fields = fieldsQuery.data ?? [];
  const aliases = aliasesQuery.data ?? [];

  const fieldTotal = fields.length;
  const fieldTotalPages = Math.max(1, Math.ceil(fieldTotal / PAGE_SIZE));
  const fieldStart = (fieldPage - 1) * PAGE_SIZE;
  const fieldPageItems = fields.slice(fieldStart, fieldStart + PAGE_SIZE);

  const aliasTotal = aliases.length;
  const aliasTotalPages = Math.max(1, Math.ceil(aliasTotal / PAGE_SIZE));
  const aliasStart = (aliasPage - 1) * PAGE_SIZE;
  const aliasPageItems = aliases.slice(aliasStart, aliasStart + PAGE_SIZE);

  const openAddField = () => {
    if (isViewer) return;
    setEditingField(undefined);
    setFieldDialogOpen(true);
  };
  const openEditField = (f: EsOntologyField) => {
    setEditingField(f);
    setFieldDialogOpen(true);
  };
  const onDeleteField = async (f: EsOntologyField) => {
    if (!f.id) return;
    try {
      await deleteField.mutateAsync({ id: f.id, projectUuid, dataLakeId });
    } catch {
      /* toast in mutation */
    }
  };

  const openAddAlias = () => {
    if (isViewer) return;
    setEditingAlias(undefined);
    setAliasDialogOpen(true);
  };
  const openEditAlias = (a: EsOntologyAlias) => {
    setEditingAlias(a);
    setAliasDialogOpen(true);
  };
  const onDeleteAlias = async (a: EsOntologyAlias) => {
    if (!a.id) return;
    try {
      await deleteAlias.mutateAsync({ id: a.id, projectUuid, dataLakeId });
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <>
      {/* Fields card */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">Fields</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${fieldPageItems.length} of ${fieldTotal}`}
            </span>
            {indexName && (
              <span className="text-sm text-white truncate" title={indexName}>
                — {indexName}
              </span>
            )}
            {fieldsQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add Field">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddField();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {fieldPageItems.length > 0 ? (
          <div className="space-y-1.5">
            {fieldPageItems.map((f) => (
              <div
                key={f.id ?? f.fullPath}
                className="relative group/field flex items-start justify-between gap-2 border border-surface-border rounded bg-surface p-2.5 text-white"
              >
                <div className="min-w-0">
                  <div className="text-sm font-mono truncate" title={f.fullPath}>
                    {f.fullPath}
                  </div>
                  <div className="text-xs text-muted">
                    {f.type}
                    {f.isMultiField ? " · multi-field" : ""}
                    {f.isNested ? " · nested" : ""}
                    {f.isObject ? " · object" : ""}
                    {f.analyzer ? ` · analyzer:${f.analyzer}` : ""}
                  </div>
                </div>
                {!isViewer && (
                  <div className="flex gap-1.5 shrink-0 items-center opacity-0 group-hover/field:opacity-100 transition-opacity">
                    <CustomTooltip text="Edit Field">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted hover:text-white hover:bg-surface-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditField(f);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                    <CustomTooltip text="Delete Field">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!f.id}
                        className="h-7 w-7 p-0 text-muted hover:text-error hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteField(f);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : fieldsQuery.isFetching ? (
          <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading fields...
          </div>
        ) : (
          <div className="text-xs text-muted text-center py-2">
            No fields yet. Click + to add one.
          </div>
        )}

        {fieldTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {fieldPage} of {fieldTotalPages} ({fieldPageItems.length} fields this page) • Total: {fieldTotal} fields
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFieldPage((p) => Math.max(1, p - 1))}
                disabled={fieldPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFieldPage((p) => Math.min(fieldTotalPages, p + 1))}
                disabled={fieldPage === fieldTotalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Aliases card */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">Aliases</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${aliasPageItems.length} of ${aliasTotal}`}
            </span>
            {indexName && (
              <span className="text-sm text-white truncate" title={indexName}>
                — {indexName}
              </span>
            )}
            {aliasesQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add Alias">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddAlias();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {aliasPageItems.length > 0 ? (
          <div className="space-y-1.5">
            {aliasPageItems.map((a) => (
              <div
                key={a.id ?? a.name}
                className="relative group/alias flex items-start justify-between gap-2 border border-surface-border rounded bg-surface p-2.5 text-white"
              >
                <div className="min-w-0">
                  <div className="text-sm font-mono truncate" title={a.name}>
                    {a.name}
                  </div>
                  <div className="text-xs text-muted">
                    {a.isWriteIndex ? "write-index" : "read-index"}
                    {a.filter ? " · filtered" : ""}
                  </div>
                </div>
                {!isViewer && (
                  <div className="flex gap-1.5 shrink-0 items-center opacity-0 group-hover/alias:opacity-100 transition-opacity">
                    <CustomTooltip text="Edit Alias">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted hover:text-white hover:bg-surface-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditAlias(a);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                    <CustomTooltip text="Delete Alias">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!a.id}
                        className="h-7 w-7 p-0 text-muted hover:text-error hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteAlias(a);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : aliasesQuery.isFetching ? (
          <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading aliases...
          </div>
        ) : (
          <div className="text-xs text-muted text-center py-2">
            No aliases yet. Click + to add one.
          </div>
        )}

        {aliasTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {aliasPage} of {aliasTotalPages} ({aliasPageItems.length} aliases this page) • Total: {aliasTotal} aliases
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAliasPage((p) => Math.max(1, p - 1))}
                disabled={aliasPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAliasPage((p) => Math.min(aliasTotalPages, p + 1))}
                disabled={aliasPage === aliasTotalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <EsFieldDialog
        projectUuid={projectUuid}
        isOpen={fieldDialogOpen}
        onClose={() => setFieldDialogOpen(false)}
        dataLakeId={dataLakeId}
        indexId={indexId}
        indexName={indexName}
        existing={editingField}
      />
      <EsAliasDialog
        projectUuid={projectUuid}
        isOpen={aliasDialogOpen}
        onClose={() => setAliasDialogOpen(false)}
        dataLakeId={dataLakeId}
        indexId={indexId}
        indexName={indexName}
        existing={editingAlias}
      />
    </>
  );
}
